/*
 * DossierDrawer — slide-in panel from the right that surfaces the full
 * dossier on demand. Reuses <DossierPanel> internally; this file is just
 * the overlay/transition shell + open/close behavior.
 *
 * Triggered by the "X of N captured" pill at the bottom of the chat
 * surface. Doesn't replace the chat — complements it.
 */

import React, { useEffect } from 'react';
import { X } from 'lucide-react';

import { DossierPanel } from './DossierPanel';
import type {
  CompanyContext,
  FieldDef,
  SectionLetter,
} from '../lib/dossier-types';


export interface DossierDrawerProps {
  open: boolean;
  onClose: () => void;
  fields: FieldDef[];
  sections: Partial<Record<SectionLetter, string>>;
  ctx: CompanyContext;
  activeField: string | null;
  capturedCount: number;
  totalCount: number;
  onCommit: (fieldKey: string, value: unknown, action: 'confirm' | 'correct') => void;
}


export function DossierDrawer({
  open,
  onClose,
  fields,
  sections,
  ctx,
  activeField,
  capturedCount,
  totalCount,
  onCommit,
}: DossierDrawerProps) {
  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`aa-drawer-scrim${open ? ' aa-drawer-scrim--on' : ''}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        className={`aa-drawer${open ? ' aa-drawer--on' : ''}`}
        aria-hidden={!open}
        aria-label="Company dossier"
      >
        <header className="aa-drawer-header">
          {/* The scoped count lives in DossierPanel's own header below —
              showing the full-universe tally here too was the misleading
              "60" the user saw on the problem route. Just a title here. */}
          <div className="aa-drawer-title">
            <span className="aa-drawer-count-label">Dossier</span>
          </div>
          <button
            type="button"
            className="aa-drawer-close"
            onClick={onClose}
            aria-label="Close dossier"
          >
            <X size={16} />
          </button>
        </header>

        <div className="aa-drawer-body">
          <DossierPanel
            fields={fields}
            sections={sections}
            ctx={ctx}
            activeField={activeField}
            capturedCount={capturedCount}
            totalCount={totalCount}
            onCommit={onCommit}
          />
        </div>
      </aside>
    </>
  );
}

export default DossierDrawer;
